import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * Formata uma data para exibição
 * @param dateString - String da data no formato ISO
 * @param formatStr - Formato desejado (padrão: dd/MM/yyyy)
 * @returns String formatada da data ou string vazia se inválida
 */
export const formatDate = (dateString: string | Date | null | undefined, formatStr = "dd/MM/yyyy"): string => {
  if (!dateString) return "";
  try {
    let date: Date;
    if (typeof dateString === "string") {
      // Se parece uma ISO string, usa parseISO
      if (dateString.includes("T") || dateString.includes("-")) {
        date = parseISO(dateString);
      } else {
        date = new Date(dateString);
      }
    } else {
      date = dateString;
    }
    if (!isValid(date)) return "";
    return format(date, formatStr, { locale: ptBR });
  } catch (error) {
    return "";
  }
};

/**
 * Formata uma data e hora para exibição
 * @param dateString - String da data no formato ISO
 * @param formatStr - Formato desejado (padrão: dd/MM/yyyy HH:mm)
 * @returns String formatada da data e hora ou string vazia se inválida
 */
export const formatDateTime = (dateString: string | Date | null | undefined, formatStr = "dd/MM/yyyy HH:mm"): string => {
  return formatDate(dateString, formatStr);
};

/**
 * Formata um valor monetário
 * @param value - Valor numérico
 * @returns String formatada do valor monetário
 */
export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

/**
 * Formata um valor monetário a partir de uma string
 * @param value - String contendo um valor numérico
 * @returns String formatada do valor monetário
 */
export const formatCurrencyFromString = (value: string | null | undefined): string => {
  if (!value) return "R$ 0,00";
  const numericValue = parseFloat(value.replace(/[^0-9,.-]/g, "").replace(",", "."));
  return isNaN(numericValue) ? "R$ 0,00" : formatCurrency(numericValue);
};

/**
 * Converte uma string de valor monetário para número
 * @param value - String contendo um valor monetário
 * @returns Valor numérico ou 0 se inválido
 */
export const parseFormattedCurrency = (value: string | null | undefined): number => {
  if (!value) return 0;
  const numericValue = parseFloat(value.replace(/[^0-9,.-]/g, "").replace(",", "."));
  return isNaN(numericValue) ? 0 : numericValue;
};

/**
 * Formata um número com separadores
 * @param value - Valor numérico
 * @returns String formatada com separadores
 */
export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("pt-BR").format(value);
};

/**
 * Formata um número de CPF
 * @param value - Valor do CPF (apenas números)
 * @returns String formatada do CPF
 */
export const formatCPF = (value: string | null | undefined): string => {
  if (!value) return "";
  
  // Remove caracteres não numéricos
  const numeros = value.replace(/\D/g, "").slice(0, 11);
  
  // Aplica a máscara de CPF
  if (numeros.length === 11) {
    return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  
  return numeros;
};

/**
 * Formata um número de CPF/CNPJ
 * @param value - Valor do CPF/CNPJ (apenas números)
 * @returns String formatada do CPF/CNPJ
 */
export const formatCpfCnpj = (value: string | null | undefined): string => {
  if (!value) return "";
  
  // Remove caracteres não numéricos
  const numeros = value.replace(/\D/g, "");
  
  // Verifica se é CPF ou CNPJ pelo tamanho
  if (numeros.length === 11) {
    // CPF: 000.000.000-00
    return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  } else if (numeros.length === 14) {
    // CNPJ: 00.000.000/0000-00
    return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  
  return numeros;
};

/**
 * Formata um número de telefone
 * @param value - Valor do telefone (apenas números)
 * @returns String formatada do telefone
 */
export const formatPhone = (value: string | null | undefined): string => {
  if (!value) return "";
  
  // Remove caracteres não numéricos e limita a 11 dígitos
  const numeros = value.replace(/\D/g, "").slice(0, 11);
  
  // Verifica o tamanho para aplicar a formatação correta
  if (numeros.length === 11) {
    // Celular: (00) 00000-0000
    return numeros.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  } else if (numeros.length === 10) {
    // Fixo: (00) 0000-0000
    return numeros.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  } else if (numeros.length >= 6) {
    // Formatação parcial para números incompletos
    if (numeros.length <= 8) {
      // (00) 0000
      return numeros.replace(/(\d{2})(\d{0,6})/, "($1) $2");
    } else {
      // (00) 00000-000
      return numeros.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
    }
  }
  
  return numeros;
};

/**
 * Formata um número de telefone (alias para formatPhone)
 */
export const formatPhoneNumber = formatPhone;

/**
 * Formata um CEP
 * @param value - Valor do CEP (apenas números)
 * @returns String formatada do CEP
 */
export const formatCEP = (value: string | null | undefined): string => {
  if (!value) return "";
  
  // Remove caracteres não numéricos
  const numeros = value.replace(/\D/g, "").slice(0, 8);
  
  // Aplica a máscara de CEP
  if (numeros.length === 8) {
    return numeros.replace(/(\d{5})(\d{3})/, "$1-$2");
  }
  
  return numeros;
};

/**
 * Formata o tamanho de um arquivo em bytes, KB, MB, etc.
 * @param bytes - Tamanho em bytes
 * @returns String formatada do tamanho
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 bytes";
  
  const sizes = ["bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
  if (i === 0) return `${bytes} ${sizes[i]}`;
  
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
}; 